import {useCallback, useEffect} from "react";
import {css, cva, cx} from "@panda/css";

import {WideContent} from "@pg-design/grid-module";
import {Building3DViewIcon} from "@pg-design/icons-module";
import {Picture} from "@pg-design/picture-module";
import {themeRP} from "@pg-design/themes";
import {useFetch} from "@pg-mono/hooks";
import {head} from "@pg-mono/nodash";
import {OFFER_TEST_IDS} from "@pg-mono/test-ids";
import {useUserDevice} from "@pg-mono/user-device";

import {ApplicationSource} from "../../../application/utils/form/application_form_source";
import {GalleryCount} from "../../../atoms/GalleryCount";
import {GalleryVideoIcon} from "../../../atoms/GalleryVideoIcon";
import {propertyPlan2dTracking} from "../../../property/tracking/property_plans2d_tracking";
import {IPropertyDetail} from "../../../property/types/IPropertyDetail";
import {IPropertyDetailOffer} from "../../../property/types/IPropertyDetailOffer";
import {getPropertyPlanAltText} from "../../../property/utils/get_property_plan_modal_title";
import {RankingOfferBadge} from "../../../ranking/components/RankingOfferBadge";
import {useGetReelsListQuery} from "../../../reels/api/get_reels_list";
import {getRequestStateFromRtkQuery} from "../../../store/utils/get_request_state_from_rtk_query";
import {getThumbnailLink} from "../../../utils/get_thumbnail_link";
import {fetchOfferGalleryPictures, fetchPropertyGalleryPictures} from "../../actions/fetch_gallery_pictures";
import {useGetOfferConstructionGalleryQuery} from "../../api/get_offer_construction_gallery";
import {IOfferBuilding} from "../../detail/types/IOfferBuilding";
import {IOfferDetail} from "../../types/IOfferDetail";
import {OfferModalActiveTab} from "../../types/OfferModalActiveTab";
import {hasOffer3dView} from "../../utils/has_offer_3d_view";
import {GalleryHolderModal} from "./GalleryHolderModal";
import {GalleryHolderReel} from "./GalleryHolderReel";
import {GalleryHolderRightSection} from "./GalleryHolderRightSection";

const presaleOfferDefaultImg375x212 = require("../../images/presale_offer_default_img_375x212.jpg");
const presaleOfferDefaultImg = require("../../images/presale_offer_default_img.jpg");

interface IProps {
    handleGalleryModalChange: (tab: OfferModalActiveTab) => void;
    modalState: OfferModalActiveTab | null;
    source: ApplicationSource;
    offer: IPropertyDetailOffer | IOfferDetail;
    offerBuildings?: IOfferBuilding[];
    property?: IPropertyDetail;
    onPlanImageOpen?: () => void;
    onModalClose?: () => void;
    isOpen: boolean;
    amplitudeGalleryLocation?: string;
}

export const GalleryHolder = (props: IProps) => {
    const {offer, property, handleGalleryModalChange, isOpen, modalState, source, offerBuildings, onModalClose, amplitudeGalleryLocation} = props;

    const externalPlanUrl = property?.external_plan_url;
    const altText = getPropertyPlanAltText(offer.name, property);
    const {
        fetchStart: fetchOfferGalleryPicture,
        response: galleryPictures,
        fetchState: galleryFetchState
    } = useFetch(() => fetchOfferGalleryPictures(offer.id));
    const fetchPropertyGalleryPicturesCallback = useCallback(() => {
        if (property?.id) {
            return fetchPropertyGalleryPictures(property.id);
        }
        return Promise.resolve(null);
    }, [property?.id]);

    const {
        fetchStart: fetchPropertyGalleryPicture,
        response: propertyGalleryPictures,
        fetchState: propertyGalleryFetchState
    } = useFetch(fetchPropertyGalleryPicturesCallback);

    const {
        data: constructionGalleryResponse,
        isLoading: isConstructionGalleryLoading,
        isFetching: isConstructionGalleryFetching,
        isSuccess: isConstructionGallerySuccess,
        isError: isConstructionGalleryError
    } = useGetOfferConstructionGalleryQuery(offer.id);
    const constructionFetchState = getRequestStateFromRtkQuery({
        isLoading: isConstructionGalleryLoading,
        isFetching: isConstructionGalleryFetching,
        isSuccess: isConstructionGallerySuccess,
        isError: isConstructionGalleryError
    });

    useEffect(() => {
        fetchOfferGalleryPicture();
        if (property && property.id) {
            fetchPropertyGalleryPicture();
        }
    }, []);

    const firstPlanPage = head(property?.plan_image_pages || []);
    const planImage = getThumbnailLink(firstPlanPage?.image_url, 760, 428) ?? firstPlanPage?.image.p_img_760x428;

    const onRightImageClick = () => {
        if (planImage) {
            propertyPlan2dTracking.gtm.openPropertyPlan();
        }
    };

    const {isMobile} = useUserDevice();

    const isPreSale = "pre_sale" in offer.configuration && !!offer.configuration.pre_sale;

    const offerGalleryLength = galleryPictures?.length || 0;
    const propertyGalleryLength = propertyGalleryPictures?.length || 0;

    const galleryLength = offerGalleryLength + propertyGalleryLength;

    const mainImgSources =
        isPreSale && !offer.main_image
            ? [
                  {
                      minWidthPX: 0,
                      src: presaleOfferDefaultImg375x212,
                      height: 360,
                      width: 448
                  },
                  {
                      minWidthPX: themeRP.breakpoints.numeric.sm,
                      src: presaleOfferDefaultImg,
                      height: 588,
                      width: 1366
                  }
              ]
            : [
                  {
                      minWidthPX: 0,
                      src: getThumbnailLink(offer.main_image_url, 440, 248) ?? offer.main_image?.m_img_440x248,
                      height: 360,
                      width: 448
                  },
                  {
                      minWidthPX: themeRP.breakpoints.numeric.xs,
                      src: getThumbnailLink(offer.main_image_url, 760, 428) ?? offer.main_image?.m_img_760x428,
                      height: 600,
                      width: 448
                  },
                  {
                      minWidthPX: themeRP.breakpoints.numeric.sm,
                      src: getThumbnailLink(offer.main_image_url, 983, 552) ?? offer.main_image?.m_img_983x552,
                      height: 588,
                      width: 1366
                  },
                  {
                      minWidthPX: themeRP.breakpoints.numeric.md,
                      src: getThumbnailLink(offer.main_image_url, 760, 428) ?? offer.main_image?.m_img_760x428,
                      height: 826,
                      width: 1919
                  }
              ];

    const externalPlanImage = property?.external_plan_url
        ? [
              {
                  minWidthPX: 0,
                  src: property.external_plan_url,
                  height: 360,
                  width: 480
              }
          ]
        : null;
    const properyPlanImages =
        firstPlanPage?.image_url || firstPlanPage?.image
            ? [
                  {
                      minWidthPX: 0,
                      src: getThumbnailLink(firstPlanPage?.image_url, 375, 250) ?? firstPlanPage?.image?.p_img_375x250,
                      height: 360,
                      width: 448
                  },
                  {
                      minWidthPX: themeRP.breakpoints.numeric.xs,
                      src: getThumbnailLink(firstPlanPage?.image_url, 760, 428) ?? firstPlanPage?.image?.p_img_760x428,
                      height: 600,
                      width: 448
                  },
                  {
                      minWidthPX: themeRP.breakpoints.numeric.sm,
                      src: getThumbnailLink(firstPlanPage?.image_url, 980) ?? firstPlanPage?.image?.p_img_980,
                      height: 588,
                      width: 1366
                  }
              ]
            : null;

    const mainPlanImage = externalPlanImage || properyPlanImages || mainImgSources;

    const onLeftImageClick = () => {
        const initialTab = isMobile && (externalPlanImage || properyPlanImages) ? OfferModalActiveTab.PLAN : OfferModalActiveTab.GALLERY;
        handleGalleryModalChange(initialTab);
    };

    const {data: reelList} = useGetReelsListQuery({offerId: offer.id}, {skip: !!property?.id});
    const firstReel = reelList?.results?.[0] || null;
    const isReelAvailable = !!firstReel;

    return (
        <WideContent>
            <div className={cx(galleryHolderStyle, isReelAvailable && galleryHolderWithReelStyle)}>
                {isReelAvailable && (
                    <div className={galleryReelSlot}>
                        <GalleryHolderReel reelData={firstReel} offer={offer} />
                    </div>
                )}

                <button type="button" className={galleryLeftSideHolder} onClick={onLeftImageClick} data-testid={OFFER_TEST_IDS.OFFER_VIEW.OPEN_GALLERY_BUTTON}>
                    <Picture
                        sources={mainPlanImage}
                        alt={offer.name}
                        className={mainImageMobileStyle}
                        fetchPriority="high"
                        imageClassName={css({
                            maxHeight: "340px",
                            xs: {maxHeight: "500px"}
                        })}
                    />

                    <Picture sources={mainImgSources} alt={offer.name} className={mainImageStyle} fetchPriority="high" imageClassName={css({height: "100%"})} />

                    <RankingOfferBadge offerId={offer.id} />

                    <div className={imageCounterHolder}>
                        {hasOffer3dView(offer, constructionGalleryResponse?.construction_3d_tours) && (
                            <div className={building3dIconOnTop}>
                                <Building3DViewIcon fill="white" size="3" />
                            </div>
                        )}
                        <div className={imageCounterRow}>
                            {offer.video && <GalleryVideoIcon />}
                            <GalleryCount photosAmount={galleryLength + 1} />
                        </div>
                    </div>
                </button>

                <div
                    className={galleryRightSideHolder({
                        hasExternalPlan: Boolean(externalPlanUrl),
                        hasReelBeenShown: isReelAvailable
                    })}
                    onClick={onRightImageClick}
                >
                    <GalleryHolderRightSection
                        offer={offer}
                        planImage={planImage}
                        handleGalleryModalChange={handleGalleryModalChange}
                        galleryPictures={galleryPictures}
                        galleryFetchState={galleryFetchState}
                        propertyGalleryPictures={propertyGalleryPictures}
                        externalPlan={externalPlanUrl}
                        displayOnlyOneColumn={isReelAvailable}
                        planImageAltText={altText}
                        construction3dTours={constructionGalleryResponse?.construction_3d_tours}
                    />
                </div>
            </div>

            <GalleryHolderModal
                isOpen={isOpen}
                modalState={modalState}
                source={source}
                offerBuildings={offerBuildings}
                onModalClose={onModalClose}
                galleryPictures={galleryPictures}
                propertyGalleryPictures={propertyGalleryPictures}
                constructionGalleryResponse={constructionGalleryResponse}
                galleryFetchState={galleryFetchState}
                propertyGalleryFetchState={propertyGalleryFetchState}
                constructionFetchState={constructionFetchState}
                offer={offer}
                handleGalleryModalChange={handleGalleryModalChange}
                property={property}
                amplitudeGalleryLocation={amplitudeGalleryLocation}
            />
        </WideContent>
    );
};

const galleryHolderStyle = css({
    overflow: "hidden",
    display: "grid",
    gridTemplateColumns: "1fr",
    gridAutoRows: "auto",
    gridRowGap: 2,
    marginBottom: "1.5rem",

    "& img": {
        cursor: "pointer",
        width: "100%"
    },

    md: {
        borderRadius: 3,
        marginBottom: 5,
        gridTemplateColumns: "1fr 1fr",
        gridColumnGap: 2
    }
});

const galleryHolderWithReelStyle = css({
    borderRadius: 0,

    xs: {
        borderRadius: 2,
        gridTemplateColumns: "1fr 3.17fr",
        gridTemplateRows: "1fr",
        gridColumnGap: 2
    },

    md: {
        marginBottom: 4,
        gridTemplateColumns: "1fr 3.17fr 1.52fr"
    }
});

const galleryReelSlot = css({
    borderRadius: 2,
    position: "relative",
    aspectRatio: "259 / 462",
    overflow: "hidden",

    xs: {
        borderRadius: 0,
        aspectRatio: "auto",
        height: "100%"
    }
});

const galleryLeftSideHolder = css({
    borderRadius: 2,
    position: "relative",
    overflow: "hidden",
    height: "100%",

    xs: {
        borderRadius: 0,
        overflow: "visible"
    }
});

const mainImageMobileStyle = css({
    backgroundColor: "var(--colors-white)",

    md: {
        display: "none"
    }
});

const mainImageStyle = css({
    height: "100%",
    display: "none",

    md: {
        display: "block"
    }
});

const imageCounterHolder = css({
    position: "absolute",
    bottom: "1.2rem",
    right: "1.2rem",
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
    gap: "0.7rem"
});

const building3dIconOnTop = css({
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    borderRadius: 0.5,
    padding: 0.5,
    backgroundColor: "color-mix(in srgb, var(--colors-gray-800) 70%, transparent)",
    pointerEvents: "none",
    minWidth: 46,
    minHeight: 44
});

const imageCounterRow = css({
    display: "flex",
    alignItems: "center",
    gap: "0.7rem"
});

const galleryRightSideHolder = cva({
    base: {
        display: "none",

        md: {
            display: "initial"
        }
    },

    variants: {
        hasExternalPlan: {
            true: {
                p: 2,
                backgroundColor: "var(--colors-white)"
            },
            false: {}
        },

        hasReelBeenShown: {
            true: {
                pl: 0
            },
            false: {
                pl: 1
            }
        }
    }
});
import {useCallback, useEffect} from "react";
import {css, cva, cx} from "@panda/css";

import {WideContent} from "@pg-design/grid-module";
import {Building3DViewIcon} from "@pg-design/icons-module";
import {Picture} from "@pg-design/picture-module";
import {themeRP} from "@pg-design/themes";
import {useFetch} from "@pg-mono/hooks";
import {head} from "@pg-mono/nodash";
import {OFFER_TEST_IDS} from "@pg-mono/test-ids";
import {useUserDevice} from "@pg-mono/user-device";

import {ApplicationSource} from "../../../application/utils/form/application_form_source";
import {GalleryCount} from "../../../atoms/GalleryCount";
import {GalleryVideoIcon} from "../../../atoms/GalleryVideoIcon";
import {propertyPlan2dTracking} from "../../../property/tracking/property_plans2d_tracking";
import {IPropertyDetail} from "../../../property/types/IPropertyDetail";
import {IPropertyDetailOffer} from "../../../property/types/IPropertyDetailOffer";
import {getPropertyPlanAltText} from "../../../property/utils/get_property_plan_modal_title";
import {RankingOfferBadge} from "../../../ranking/components/RankingOfferBadge";
import {useGetReelsListQuery} from "../../../reels/api/get_reels_list";
import {getRequestStateFromRtkQuery} from "../../../store/utils/get_request_state_from_rtk_query";
import {getThumbnailLink} from "../../../utils/get_thumbnail_link";
import {fetchOfferGalleryPictures, fetchPropertyGalleryPictures} from "../../actions/fetch_gallery_pictures";
import {useGetOfferConstructionGalleryQuery} from "../../api/get_offer_construction_gallery";
import {IOfferBuilding} from "../../detail/types/IOfferBuilding";
import {IOfferDetail} from "../../types/IOfferDetail";
import {OfferModalActiveTab} from "../../types/OfferModalActiveTab";
import {hasOffer3dView} from "../../utils/has_offer_3d_view";
import {GalleryHolderModal} from "./GalleryHolderModal";
import {GalleryHolderReel} from "./GalleryHolderReel";
import {GalleryHolderRightSection} from "./GalleryHolderRightSection";

const presaleOfferDefaultImg375x212 = require("../../images/presale_offer_default_img_375x212.jpg");
const presaleOfferDefaultImg = require("../../images/presale_offer_default_img.jpg");

interface IProps {
    handleGalleryModalChange: (tab: OfferModalActiveTab) => void;
    modalState: OfferModalActiveTab | null;
    source: ApplicationSource;
    offer: IPropertyDetailOffer | IOfferDetail;
    offerBuildings?: IOfferBuilding[];
    property?: IPropertyDetail;
    onPlanImageOpen?: () => void;
    onModalClose?: () => void;
    isOpen: boolean;
    amplitudeGalleryLocation?: string;
}

export const GalleryHolder = (props: IProps) => {
    const {offer, property, handleGalleryModalChange, isOpen, modalState, source, offerBuildings, onModalClose, amplitudeGalleryLocation} = props;

    const externalPlanUrl = property?.external_plan_url;
    const altText = getPropertyPlanAltText(offer.name, property);
    const {
        fetchStart: fetchOfferGalleryPicture,
        response: galleryPictures,
        fetchState: galleryFetchState
    } = useFetch(() => fetchOfferGalleryPictures(offer.id));
    const fetchPropertyGalleryPicturesCallback = useCallback(() => {
        if (property?.id) {
            return fetchPropertyGalleryPictures(property.id);
        }
        return Promise.resolve(null);
    }, [property?.id]);

    const {
        fetchStart: fetchPropertyGalleryPicture,
        response: propertyGalleryPictures,
        fetchState: propertyGalleryFetchState
    } = useFetch(fetchPropertyGalleryPicturesCallback);

    const {
        data: constructionGalleryResponse,
        isLoading: isConstructionGalleryLoading,
        isFetching: isConstructionGalleryFetching,
        isSuccess: isConstructionGallerySuccess,
        isError: isConstructionGalleryError
    } = useGetOfferConstructionGalleryQuery(offer.id);
    const constructionFetchState = getRequestStateFromRtkQuery({
        isLoading: isConstructionGalleryLoading,
        isFetching: isConstructionGalleryFetching,
        isSuccess: isConstructionGallerySuccess,
        isError: isConstructionGalleryError
    });

    useEffect(() => {
        fetchOfferGalleryPicture();
        if (property && property.id) {
            fetchPropertyGalleryPicture();
        }
    }, []);

    const firstPlanPage = head(property?.plan_image_pages || []);
    const planImage = getThumbnailLink(firstPlanPage?.image_url, 760, 428) ?? firstPlanPage?.image.p_img_760x428;

    const onRightImageClick = () => {
        if (planImage) {
            propertyPlan2dTracking.gtm.openPropertyPlan();
        }
    };

    const {isMobile} = useUserDevice();

    const isPreSale = "pre_sale" in offer.configuration && !!offer.configuration.pre_sale;

    const offerGalleryLength = galleryPictures?.length || 0;
    const propertyGalleryLength = propertyGalleryPictures?.length || 0;

    const galleryLength = offerGalleryLength + propertyGalleryLength;

    const mainImgSources =
        isPreSale && !offer.main_image
            ? [
                  {
                      minWidthPX: 0,
                      src: presaleOfferDefaultImg375x212,
                      height: 360,
                      width: 448
                  },
                  {
                      minWidthPX: themeRP.breakpoints.numeric.sm,
                      src: presaleOfferDefaultImg,
                      height: 588,
                      width: 1366
                  }
              ]
            : [
                  {
                      minWidthPX: 0,
                      src: getThumbnailLink(offer.main_image_url, 440, 248) ?? offer.main_image?.m_img_440x248,
                      height: 360,
                      width: 448
                  },
                  {
                      minWidthPX: themeRP.breakpoints.numeric.xs,
                      src: getThumbnailLink(offer.main_image_url, 760, 428) ?? offer.main_image?.m_img_760x428,
                      height: 600,
                      width: 448
                  },
                  {
                      minWidthPX: themeRP.breakpoints.numeric.sm,
                      src: getThumbnailLink(offer.main_image_url, 983, 552) ?? offer.main_image?.m_img_983x552,
                      height: 588,
                      width: 1366
                  },
                  {
                      minWidthPX: themeRP.breakpoints.numeric.md,
                      src: getThumbnailLink(offer.main_image_url, 760, 428) ?? offer.main_image?.m_img_760x428,
                      height: 826,
                      width: 1919
                  }
              ];

    const externalPlanImage = property?.external_plan_url
        ? [
              {
                  minWidthPX: 0,
                  src: property.external_plan_url,
                  height: 360,
                  width: 480
              }
          ]
        : null;
    const properyPlanImages =
        firstPlanPage?.image_url || firstPlanPage?.image
            ? [
                  {
                      minWidthPX: 0,
                      src: getThumbnailLink(firstPlanPage?.image_url, 375, 250) ?? firstPlanPage?.image?.p_img_375x250,
                      height: 360,
                      width: 448
                  },
                  {
                      minWidthPX: themeRP.breakpoints.numeric.xs,
                      src: getThumbnailLink(firstPlanPage?.image_url, 760, 428) ?? firstPlanPage?.image?.p_img_760x428,
                      height: 600,
                      width: 448
                  },
                  {
                      minWidthPX: themeRP.breakpoints.numeric.sm,
                      src: getThumbnailLink(firstPlanPage?.image_url, 980) ?? firstPlanPage?.image?.p_img_980,
                      height: 588,
                      width: 1366
                  }
              ]
            : null;

    const mainPlanImage = externalPlanImage || properyPlanImages || mainImgSources;

    const onLeftImageClick = () => {
        const initialTab = isMobile && (externalPlanImage || properyPlanImages) ? OfferModalActiveTab.PLAN : OfferModalActiveTab.GALLERY;
        handleGalleryModalChange(initialTab);
    };

    const {data: reelList} = useGetReelsListQuery({offerId: offer.id}, {skip: !!property?.id});
    const firstReel = reelList?.results?.[0] || null;
    const isReelAvailable = !!firstReel;

    return (
        <WideContent>
            <div className={cx(galleryHolderStyle, isReelAvailable && galleryHolderWithReelStyle)}>
                {isReelAvailable && (
                    <div className={galleryReelSlot}>
                        <GalleryHolderReel reelData={firstReel} offer={offer} />
                    </div>
                )}

                <button type="button" className={galleryLeftSideHolder} onClick={onLeftImageClick} data-testid={OFFER_TEST_IDS.OFFER_VIEW.OPEN_GALLERY_BUTTON}>
                    <Picture
                        sources={mainPlanImage}
                        alt={offer.name}
                        className={mainImageMobileStyle}
                        fetchPriority="high"
                        imageClassName={css({
                            maxHeight: "340px",
                            xs: {maxHeight: "500px"}
                        })}
                    />

                    <Picture sources={mainImgSources} alt={offer.name} className={mainImageStyle} fetchPriority="high" imageClassName={css({height: "100%"})} />

                    <RankingOfferBadge offerId={offer.id} />

                    <div className={imageCounterHolder}>
                        {hasOffer3dView(offer, constructionGalleryResponse?.construction_3d_tours) && (
                            <div className={building3dIconOnTop}>
                                <Building3DViewIcon fill="white" size="3" />
                            </div>
                        )}
                        <div className={imageCounterRow}>
                            {offer.video && <GalleryVideoIcon />}
                            <GalleryCount photosAmount={galleryLength + 1} />
                        </div>
                    </div>
                </button>

                <div
                    className={galleryRightSideHolder({
                        hasExternalPlan: Boolean(externalPlanUrl),
                        hasReelBeenShown: isReelAvailable
                    })}
                    onClick={onRightImageClick}
                >
                    <GalleryHolderRightSection
                        offer={offer}
                        planImage={planImage}
                        handleGalleryModalChange={handleGalleryModalChange}
                        galleryPictures={galleryPictures}
                        galleryFetchState={galleryFetchState}
                        propertyGalleryPictures={propertyGalleryPictures}
                        externalPlan={externalPlanUrl}
                        displayOnlyOneColumn={isReelAvailable}
                        planImageAltText={altText}
                        construction3dTours={constructionGalleryResponse?.construction_3d_tours}
                    />
                </div>
            </div>

            <GalleryHolderModal
                isOpen={isOpen}
                modalState={modalState}
                source={source}
                offerBuildings={offerBuildings}
                onModalClose={onModalClose}
                galleryPictures={galleryPictures}
                propertyGalleryPictures={propertyGalleryPictures}
                constructionGalleryResponse={constructionGalleryResponse}
                galleryFetchState={galleryFetchState}
                propertyGalleryFetchState={propertyGalleryFetchState}
                constructionFetchState={constructionFetchState}
                offer={offer}
                handleGalleryModalChange={handleGalleryModalChange}
                property={property}
                amplitudeGalleryLocation={amplitudeGalleryLocation}
            />
        </WideContent>
    );
};

const galleryHolderStyle = css({
    overflow: "hidden",
    display: "grid",
    gridTemplateColumns: "1fr",
    gridAutoRows: "auto",
    gridRowGap: 2,
    marginBottom: "1.5rem",

    "& img": {
        cursor: "pointer",
        width: "100%"
    },

    md: {
        borderRadius: 3,
        marginBottom: 5,
        gridTemplateColumns: "1fr 1fr",
        gridColumnGap: 2
    }
});

const galleryHolderWithReelStyle = css({
    borderRadius: 0,

    xs: {
        borderRadius: 2,
        gridTemplateColumns: "1fr 3.17fr",
        gridTemplateRows: "1fr",
        gridColumnGap: 2
    },

    md: {
        marginBottom: 4,
        gridTemplateColumns: "1fr 3.17fr 1.52fr"
    }
});

const galleryReelSlot = css({
    borderRadius: 2,
    position: "relative",
    aspectRatio: "259 / 462",
    overflow: "hidden",

    xs: {
        borderRadius: 0,
        aspectRatio: "auto",
        height: "100%"
    }
});

const galleryLeftSideHolder = css({
    borderRadius: 2,
    position: "relative",
    overflow: "hidden",
    height: "100%",

    xs: {
        borderRadius: 0,
        overflow: "visible"
    }
});

const mainImageMobileStyle = css({
    backgroundColor: "var(--colors-white)",

    md: {
        display: "none"
    }
});

const mainImageStyle = css({
    height: "100%",
    display: "none",

    md: {
        display: "block"
    }
});

const imageCounterHolder = css({
    position: "absolute",
    bottom: "1.2rem",
    right: "1.2rem",
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
    gap: "0.7rem"
});

const building3dIconOnTop = css({
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    borderRadius: 0.5,
    padding: 0.5,
    backgroundColor: "color-mix(in srgb, var(--colors-gray-800) 70%, transparent)",
    pointerEvents: "none",
    minWidth: 46,
    minHeight: 44
});

const imageCounterRow = css({
    display: "flex",
    alignItems: "center",
    gap: "0.7rem"
});

const galleryRightSideHolder = cva({
    base: {
        display: "none",

        md: {
            display: "initial"
        }
    },

    variants: {
        hasExternalPlan: {
            true: {
                p: 2,
                backgroundColor: "var(--colors-white)"
            },
            false: {}
        },

        hasReelBeenShown: {
            true: {
                pl: 0
            },
            false: {
                pl: 1
            }
        }
    }
});
